import React, { MutableRefObject, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAccount, useChainId, useDisconnect, useReadContract } from 'wagmi';
import { formatEther, zeroAddress } from 'viem';
import { fetchPlayer } from './Players';
import LatestSlaps from '../slaps/LatestSlaps';
import LatestStickers from '../stickers/LatestStickers';
import {
  formatEthAddress,
  getPrimaryExplorerUrlForAddress,
} from '../utils/GeneralUtilities';
import {
  Board,
  PlaceId,
  SlapId,
  StickerDesignId,
} from '../utils/Primitives';
import { abi as payoutMethodAbi } from '../payments/PayoutMethodAbi';

const PayoutMethodAddress =
  process.env
    .NEXT_PUBLIC_PUBLISHER_PAYOUT_CONTRACT_ADDRESS as `0x${string}` | null;

type PlayerReviewerContentProps = {
  playerAddress: string | null;
  boardRef: MutableRefObject<Board>;
  handleReviewPlace: (placeId: PlaceId) => void;
  handleReviewSticker: (stickerId: StickerDesignId) => void;
  handleReviewSlap: (slapId: SlapId) => void;
  startPayoutView: () => void;
  startPublishingView: () => void;
  onClose: () => void;
};

export default function PlayerReviewerContent({
  playerAddress,
  boardRef,
  handleReviewPlace,
  handleReviewSticker,
  handleReviewSlap,
  startPayoutView,
  startPublishingView,
  onClose,
}: PlayerReviewerContentProps) {
  const chainId = useChainId();
  const { address: loggedInAddress } = useAccount();
  const { disconnect } = useDisconnect();

  const showingLoggedInPlayer =
    playerAddress && loggedInAddress
      ? playerAddress === loggedInAddress
      : false;

  const { data: payoutBalanceData, isLoading: isPayoutBalanceLoading } =
    useReadContract({
      address: PayoutMethodAddress || undefined,
      abi: payoutMethodAbi,
      functionName: 'balanceOf',
      args:
        showingLoggedInPlayer && loggedInAddress
          ? [loggedInAddress, zeroAddress]
          : undefined,
    });

  const payoutBalance = useMemo(() => {
    if (!payoutBalanceData) return null;
    const numericBalance = Number(formatEther(payoutBalanceData as bigint));
    if (numericBalance <= 0) return null;
    return numericBalance.toLocaleString(undefined, {
      maximumFractionDigits: 6,
    });
  }, [payoutBalanceData]);

  const [copied, setCopied] = useState(false);
  const [slapsCurrentPage, setSlapsCurrentPage] = useState(1);
  const [stickersCurrentPage, setStickersCurrentPage] = useState(1);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [activePanel, setActivePanel] = useState<'slaps' | 'stickers'>('slaps');

  useEffect(() => {
    return () => {
      setShowLogoutConfirm(false);
    };
  }, []);

  const player = playerAddress
    ? fetchPlayer({ boardRef, address: playerAddress })
    : null;

  const explorerUrl = playerAddress
    ? getPrimaryExplorerUrlForAddress(chainId, playerAddress)
    : '';

  const avatarUrl = playerAddress
    ? `https://api.ensdata.net/media/avatar/${playerAddress}`
    : '';

  const apiUrl = process.env.NEXT_PUBLIC_STICKERCLOUD_API;

  const slapsUrl =
    apiUrl && playerAddress
      ? `${apiUrl}slaps?playerAddress=${playerAddress}`
      : '';

  const stickersUrl =
    apiUrl && playerAddress
      ? `${apiUrl}sticker-designs?originalPublisher=${playerAddress}`
      : '';

  const { data: slapsData } = useQuery({
    queryKey: ['playerSlaps', playerAddress],
    queryFn: async () => {
      if (!slapsUrl) return [];
      const response = await fetch(slapsUrl);
      if (!response.ok) throw new Error('Failed to fetch slaps');
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!playerAddress && !!slapsUrl,
    staleTime: 30_000,
  });

  const { data: stickersData, isLoading: isStickersLoading } = useQuery({
    queryKey: ['playerStickers', playerAddress],
    queryFn: async () => {
      if (!stickersUrl) return [];
      const response = await fetch(stickersUrl);
      if (!response.ok) throw new Error('Failed to fetch stickers');
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!playerAddress && !!stickersUrl,
    staleTime: 30_000,
  });

  const handleCopyToClipboard = () => {
    if (!playerAddress) return;

    navigator.clipboard
      .writeText(playerAddress)
      .then(() => {
        console.log('Address copied to clipboard');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error('Could not copy text: ', err);
      });
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    disconnect();
    onClose();
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <div
      className={
        'flex w-full min-w-0 flex-col items-start md:flex-row md:py-4 ' +
        'md:space-x-4'
      }
    >
      <div className="flex w-full min-w-0 flex-col items-center space-y-5 pt-2">
        
        
        <div className="text-center w-full mb-2">
          <div className="flex w-full min-w-0">
            <div className="w-1/2 flex justify-center items-center">
              {player?.ens ? (
                <div className="text-sm text-gray-600">{player.ens}</div>
              ) : showingLoggedInPlayer ? (
                <a
                  href="https://app.ens.domains/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Get an ENS
                </a>
              ) : (
                <div className="text-sm text-gray-500">No ENS</div>
              )}
            </div>

            <div
              className={
                'w-1/2 min-w-0 flex justify-center items-center space-x-1'
              }
            >
              <a
                href={explorerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 truncate text-sm text-gray-600 hover:underline"
              >
                {formatEthAddress(playerAddress, 5) || ''}
              </a>
              <button
                onClick={handleCopyToClipboard}
                className="text-sm text-gray-600 hover:underline"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 32 32"
                  width="16"
                  height="16"
                >
                  <rect
                    x="6"
                    y="4"
                    width="18"
                    height="24"
                    rx="2"
                    fill="#E0E0E0"
                  />
                  <rect
                    x="12"
                    y="10"
                    width="18"
                    height="24"
                    rx="2"
                    fill="#A0A0A0"
                  />
                </svg>
              </button>
              {copied && (
                <span className="text-xs text-green-500 ml-2">Copied!</span>
              )}
            </div>
          </div>
        </div>


        <div className="flex w-full">
          <div className="w-1/2 flex justify-center items-center">
            {playerAddress && (
              <img
                src={avatarUrl}
                alt="Player Avatar"
                className="w-12 h-12 rounded-full"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
          </div>

          <div className="w-1/2 flex justify-center items-center">
            {showingLoggedInPlayer && !showLogoutConfirm && (
              <button
                onClick={handleLogoutClick}
                className={
                  'text-sm bg-gray-200 text-slate-700 px-3 py-1 rounded ' +
                  'transform transition-transform duration-200 hover:scale-105'
                }
              >
                Logout
              </button>
            )}
            {showingLoggedInPlayer && showLogoutConfirm && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-slate-700">Confirm:</span>
                <button
                  onClick={handleConfirmLogout}
                  className={
                    'text-sm bg-gray-200 text-slate-700 px-3 py-1 rounded ' +
                    'transform transition-transform duration-200 ' +
                    'hover:scale-105'
                  }
                >
                  OK
                </button>
                <button
                  onClick={handleCancelLogout}
                  className={
                    'text-sm bg-gray-200 text-slate-700 px-3 py-1 rounded ' +
                    'transform transition-transform duration-200 ' +
                    'hover:scale-105'
                  }
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap justify-center space-x-4">
          <div className="w-full">
            <div className="mt-4 flex w-full justify-center gap-2">
              <button
                onClick={() => setActivePanel('slaps')}
                className={
                  activePanel === 'slaps'
                    ? 'rounded-full bg-slate-900 px-4 py-2 text-xs ' +
                      'font-semibold text-white'
                    : 'rounded-full bg-slate-200 px-4 py-2 text-xs ' +
                      'font-semibold text-slate-700'
                }
              >
                Recent Slaps
              </button>
              <button
                onClick={() => setActivePanel('stickers')}
                className={
                  activePanel === 'stickers'
                    ? 'rounded-full bg-slate-900 px-4 py-2 text-xs ' +
                      'font-semibold text-white'
                    : 'rounded-full bg-slate-200 px-4 py-2 text-xs ' +
                      'font-semibold text-slate-700'
                }
              >
                Published Stickers
              </button>
            </div>
            <div className="mt-4 flex flex-col w-full">
              <div
                className={
                  'text-sm text-gray-600 w-full ' +
                  (activePanel === 'stickers' ? 'hidden' : '')
                }
              >
                {playerAddress && (
                  <LatestSlaps
                    boardRef={boardRef}
                    fetchUrl={slapsUrl}
                    slapsPerPage={5}
                    setFocusOnPlace={(place) => {
                      if (place.placeId == null) return;
                      handleReviewPlace(place.placeId);
                    }}
                    handleStickerClick={handleReviewSticker}
                    handleSlapClick={handleReviewSlap}
                    currentPage={slapsCurrentPage}
                    setCurrentPage={setSlapsCurrentPage}
                    initialData={slapsData}
                  />
                )}
              </div>
              <div
                className={
                  'text-sm text-gray-600 w-full ' +
                  (activePanel === 'slaps' ? 'hidden' : '')
                }
              >

              {showingLoggedInPlayer &&
                !isStickersLoading &&
                (stickersData?.length ?? 0) > 0 && (
                  <div className="flex w-full flex-col items-center justify-center gap-1">
                    {!isPayoutBalanceLoading && payoutBalance ? (
                      <div className="text-sm mt-2 text-slate-600">
                        Payout Balance: {payoutBalance} ETH
                                          </div>
                                        ) : (
                                          <div className="text-sm mt-2 text-slate-600">
                                            Payout Balance:
                                          </div>
                                        )}
                                      <button
                                        onClick={() => {
                                          setShowLogoutConfirm(false);
                                          startPayoutView();
                                        }}
                                        className={
                                          'px-5 py-2 rounded-full font-medium text-sm bg-blue-300 ' +
                                          'text-slate-700 transform transition-transform duration-200 ' +
                                          'hover:scale-105'
                                        }
                                      >
                                        Publisher Payout
                                      </button>
                                    </div>
                                  )}


                {playerAddress &&
                showingLoggedInPlayer &&
                !isStickersLoading &&
                stickersData?.length === 0 ? (
                  <div className="flex w-full flex-col items-center gap-3 py-6">
                    <div className="text-sm text-slate-600">
                      You have not published any stickers yet.
                    </div>
                    <button
                      onClick={() => {
                        onClose();
                        startPublishingView();
                      }}
                      className="rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition"
                    >
                      Publish a Sticker
                    </button>
                  </div>
                ) : (
                  playerAddress && (
                    <LatestStickers
                      boardRef={boardRef}
                      fetchUrl={stickersUrl}
                      stickersPerPage={5}
                      setPreviewStickerId={handleReviewSticker}
                      currentPage={stickersCurrentPage}
                      setCurrentPage={setStickersCurrentPage}
                      initialData={stickersData}
                    />
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PlayerReviewerLoading() {
    return (
        <div className="flex flex-col items-center space-y-4">
            <div className="text-center mt-4">
                <div className="mt-6 flex flex-wrap justify-center space-x-4">
                    <div className="flex flex-col space-y-2">
                        <p className="text-sm text-gray-600"></p>
                        <p className="text-sm text-gray-600"></p>
                    </div>
                </div>
            </div>
        </div>
    );
}
